import React, { Fragment, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { history } from '../../';

import { isEqual } from 'lodash';
import { isWidthDown } from '@material-ui/core/withWidth';
import Grid from '@material-ui/core/Grid';
import pushToGTM, { pageLoadGA4Tag } from '../../utils/analytics/gtm';
import { tabRoutes } from '../../routes';
import tabbarSlice from '../../components/Tabbar/tabbarSlice';
import designListSlice from '../../redux/slices/designListSlice';
import { useStyles } from './styles';
import SidepanelLayout from '../SidepanelLayout';
import SummaryActionbar from '../../components/SummaryActionbar';
import InputParameters from '../../components/InputParameters';
import DesignSpecifications from '../../components/DesignSpecifications';
import BOMTable from '../../components/BomTable';
import OperatingValues from '../../components/OperatingValues';
import Charts from '../../components/Charts';
import DetailedSchematic from '../../components/DetailedSchematic';
import useFetchData from '../../hooks/useFetchData';
import ErrorPage from '../ErrorPage';
import { localView, localUserLoginAttempt, localLoginButtonView } from '../../utils/analytics/local';

const SummaryPage = ({ screenWidth }) => {
	const classes = useStyles();
	const [opValuesCategory, setOpValuesCategory] = useState('SYSTEM_INFO');
	const disabledTabs = useSelector(state => state.tabbar.disabledTabs);
	const fetchDesignData = useSelector(state => state.selectedDesign.fetchDesignData);
	const inputParametersSD = useSelector(state => state.selectedDesign.detail.inputParameters);
	const inputParametersDL = useSelector(state => state.designList.inputParameters);
	const icPartNumber = useSelector(state => state.selectedDesign.icPartNumber);
	const topology = useSelector(state => state.selectedDesign.topology);
	const initialVinNom = useSelector(state => state.selectedDesign.initialInputParameters.vinNom);
	const partNumber = useSelector(state => state.selectedDesign.detail.rankingParameters.partNumber);
	const progressActive = useSelector(state => state.tabbar.progressActive);
	const userLoggedIn = useSelector(state => state.user.loggedIn);
	const dispatch = useDispatch();

	const hasError = useFetchData(fetchDesignData, disabledTabs);

	useEffect(() => {
		if (disabledTabs.Summary) {
			if (disabledTabs.Select) {
				history.push(tabRoutes.find(route => route.path === '/inputs').path);
			} else {
				history.push(tabRoutes.find(route => route.path === '/select').path);
			}
		} else {
			if (!fetchDesignData) {
				localView('Summary', partNumber);
				pushToGTM(pageLoadGA4Tag('SUMMARY'));
				dispatch(tabbarSlice.actions.setProgressActive(false));
				if (!userLoggedIn) {
					localLoginButtonView(icPartNumber);
				}
			}
		}
	}, [dispatch, disabledTabs, fetchDesignData]);

	const logIn = () => {
		const fullParams = {
			...inputParametersDL,
			topology: topology,
			ic: icPartNumber
		};
		const query = Object.entries(fullParams)
			.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
			.join('&');

		localStorage.setItem('loginAttempted', Date.now().toString());
		localUserLoginAttempt(icPartNumber);
		let url = window.location.origin + '/design-tools/inputs';
		if (query) {
			url += '?' + query;
		}
		const encodedUrl = encodeURIComponent(url);
		localStorage.setItem('designAfterLoggedIn', encodedUrl);
	};

	useEffect(() => {
		if (!inputParametersSD || Object.keys(inputParametersSD).length === 0) {
			return;
		}
		if (disabledTabs.Select) {
			dispatch(tabbarSlice.actions.enableSelectTab());
			dispatch(designListSlice.actions.setFetchDesignListData(true));
			return;
		}

		const filteredSD = Object.keys(inputParametersDL).reduce((acc, key) => {
			if (key === 'vinNom') {
				acc[key] = inputParametersSD.vinNomEntered ? inputParametersSD.vinNom : '';
			} else {
				acc[key] = inputParametersSD[key];
			}
			return acc;
		}, {});
		if (!isEqual(inputParametersDL, filteredSD)) {
			dispatch(designListSlice.actions.setInputParameters(filteredSD));
			if (disabledTabs.Select) {
				dispatch(tabbarSlice.actions.enableSelectTab());
			}
			dispatch(designListSlice.actions.setFetchDesignListData(true));
		}
	}, [inputParametersSD]);

	if (!progressActive && hasError) {
		return <ErrorPage destinationPage={'/select'} />;
	}

	return (
		<SidepanelLayout
			sidepanel={
				<Grid
					container
					direction={screenWidth === 'sm' ? 'row' : 'column'}
					spacing={isWidthDown('sm', screenWidth) ? 2 : 3}>
					<Grid item container xs={screenWidth === 'sm' ? 6 : false}>
						<InputParameters inputParameters={inputParametersSD} isVinNomSet={initialVinNom !== ''} />
					</Grid>

					<Grid item container xs={screenWidth === 'sm' ? 6 : false}>
						<DesignSpecifications />
					</Grid>
				</Grid>
			}
			content={
				<Fragment>
					<SummaryActionbar selectedOpValueCategory={opValuesCategory} />

					<DetailedSchematic page={'SUMMARY'} withTitle logIn={logIn} />
					<div className={classes.container}>
						<BOMTable logIn={logIn} />
					</div>

					<div className={classes.container}>
						<OperatingValues
							opValuesCategory={opValuesCategory}
							setOpValuesCategory={setOpValuesCategory}
							withTitle
							screenWidth={screenWidth}
							id="OperatingValues"
							logIn={logIn}
						/>
					</div>

					<div className={classes.container}>
						<Charts page={'DESIGN'} withTitle screenWidth={screenWidth} />
					</div>
				</Fragment>
			}
		/>
	);
};

SummaryPage.propTypes = {
	screenWidth: PropTypes.number.isRequired
};

export default SummaryPage;
